import React, { useState, useEffect } from 'react';
import { X, Search, Download, Star, Clock, Users, Zap, FileText } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:8001";
const API = `${BACKEND_URL}/api`;

const TemplatesModal = ({ onClose, onSelectTemplate }) => {
  const [templates, setTemplates] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [loading, setLoading] = useState(false);

  const templateCategories = [
    { id: 'all', name: 'All Templates', icon: FileText },
    { id: 'recruitment', name: 'Recruitment', icon: Users },
    { id: 'automation', name: 'Automation', icon: Zap },
    { id: 'analytics', name: 'Analytics', icon: Clock },
  ];

  const sampleTemplates = [
    {
      id: 'candidate-screening',
      name: 'Automated Candidate Screening',
      description: 'Complete workflow for automated resume parsing, skill extraction, and initial screening',
      category: 'recruitment',
      rating: 4.8,
      uses: 1250,
      nodes: [
        { id: '1', type: 'start', position: { x: 100, y: 50 }, data: { label: 'Start' } },
        { id: '2', type: 'file', position: { x: 100, y: 150 }, data: { label: 'Parse Resume' } },
        { id: '3', type: 'ai', position: { x: 100, y: 250 }, data: { label: 'Extract Skills' } },
        { id: '4', type: 'decision', position: { x: 100, y: 350 }, data: { label: 'Skills Match?' } },
        { id: '5', type: 'notification', position: { x: 250, y: 450 }, data: { label: 'Send Interview' } },
        { id: '6', type: 'notification', position: { x: -50, y: 450 }, data: { label: 'Send Rejection' } },
        { id: '7', type: 'end', position: { x: 100, y: 550 }, data: { label: 'End' } },
      ],
      edges: [
        { id: 'e1-2', source: '1', target: '2' },
        { id: 'e2-3', source: '2', target: '3' },
        { id: 'e3-4', source: '3', target: '4' },
        { id: 'e4-5', source: '4', target: '5', sourceHandle: 'yes' },
        { id: 'e4-6', source: '4', target: '6', sourceHandle: 'no' },
        { id: 'e5-7', source: '5', target: '7' },
        { id: 'e6-7', source: '6', target: '7' },
      ],
    },
    {
      id: 'job-posting-automation',
      name: 'Multi-Platform Job Posting',
      description: 'Automatically post jobs to multiple platforms with AI-generated descriptions',
      category: 'automation',
      rating: 4.6,
      uses: 890,
      nodes: [
        { id: '1', type: 'start', position: { x: 100, y: 50 }, data: { label: 'Start' } },
        { id: '2', type: 'ai', position: { x: 100, y: 150 }, data: { label: 'Generate Description' } },
        { id: '3', type: 'loop', position: { x: 100, y: 250 }, data: { label: 'For Each Platform' } },
        { id: '4', type: 'api', position: { x: 250, y: 350 }, data: { label: 'Post Job' } },
        { id: '5', type: 'database', position: { x: 100, y: 450 }, data: { label: 'Log Result' } },
        { id: '6', type: 'end', position: { x: 100, y: 550 }, data: { label: 'End' } },
      ],
      edges: [
        { id: 'e1-2', source: '1', target: '2' },
        { id: 'e2-3', source: '2', target: '3' },
        { id: 'e3-4', source: '3', target: '4', sourceHandle: 'loop' },
        { id: 'e4-3', source: '4', target: '3' },
        { id: 'e3-5', source: '3', target: '5', sourceHandle: 'exit' },
        { id: 'e5-6', source: '5', target: '6' },
      ],
    },
    {
      id: 'candidate-nurturing',
      name: 'Candidate Nurturing Campaign',
      description: 'Automated follow-up sequence for maintaining candidate engagement',
      category: 'recruitment',
      rating: 4.7,
      uses: 567,
      nodes: [
        { id: '1', type: 'start', position: { x: 100, y: 50 }, data: { label: 'Start' } },
        { id: '2', type: 'database', position: { x: 100, y: 150 }, data: { label: 'Get Candidates' } },
        { id: '3', type: 'loop', position: { x: 100, y: 250 }, data: { label: 'For Each Candidate' } },
        { id: '4', type: 'decision', position: { x: 250, y: 350 }, data: { label: 'Last Contact?' } },
        { id: '5', type: 'notification', position: { x: 400, y: 450 }, data: { label: 'Send Update' } },
        { id: '6', type: 'end', position: { x: 100, y: 550 }, data: { label: 'End' } },
      ],
      edges: [
        { id: 'e1-2', source: '1', target: '2' },
        { id: 'e2-3', source: '2', target: '3' },
        { id: 'e3-4', source: '3', target: '4', sourceHandle: 'loop' },
        { id: 'e4-5', source: '4', target: '5', sourceHandle: 'yes' },
        { id: 'e5-3', source: '5', target: '3' },
        { id: 'e3-6', source: '3', target: '6', sourceHandle: 'exit' },
      ],
    },
  ];

  useEffect(() => {
    const fetchTemplates = async () => {
      setLoading(true);
      try {
        const response = await fetch(`${API}/workflows/templates`);
        if (response.ok) {
          const data = await response.json();
          // Convert backend templates to frontend format
          const formattedTemplates = Object.entries(data).map(([key, template]) => ({
            id: key,
            name: template.name,
            description: template.description,
            category: 'recruitment',
            rating: 4.8,
            uses: Math.floor(Math.random() * 1000) + 100,
            nodes: template.nodes,
            edges: template.edges
          }));
          setTemplates([...sampleTemplates, ...formattedTemplates]);
        } else {
          // Fallback to sample templates
          setTemplates(sampleTemplates);
        }
      } catch (error) {
        console.error('Error fetching templates:', error);
        setTemplates(sampleTemplates);
      } finally {
        setLoading(false);
      }
    };

    fetchTemplates();
  }, []);

  const filteredTemplates = templates.filter(template => {
    const matchesSearch = template.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         template.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || template.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Workflow Templates</h2>
              <p className="text-gray-600">Choose from pre-built workflow templates to get started quickly</p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        <div className="flex h-[calc(90vh-120px)]">
          {/* Left Sidebar - Categories */}
          <div className="w-64 border-r border-gray-200 p-4">
            <div className="space-y-2">
              {templateCategories.map((category) => {
                const Icon = category.icon;
                return (
                  <button
                    key={category.id}
                    onClick={() => setSelectedCategory(category.id)}
                    className={`w-full flex items-center space-x-3 px-3 py-2 rounded-lg transition-colors ${
                      selectedCategory === category.id
                        ? 'bg-blue-100 text-blue-700'
                        : 'hover:bg-gray-100'
                    }`}
                  >
                    <Icon className="h-5 w-5" />
                    <span className="font-medium">{category.name}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1 p-6 overflow-y-auto">
            {/* Search */}
            <div className="mb-6">
              <div className="relative">
                <Search className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search templates..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            {/* Templates Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {filteredTemplates.map((template) => (
                <div
                  key={template.id}
                  className="border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow cursor-pointer"
                  onClick={() => onSelectTemplate(template)}
                >
                  <div className="flex items-start justify-between mb-3">
                    <h3 className="text-lg font-semibold text-gray-900">{template.name}</h3>
                    <div className="flex items-center space-x-1">
                      <Star className="h-4 w-4 text-yellow-400 fill-current" />
                      <span className="text-sm text-gray-600">{template.rating}</span>
                    </div>
                  </div>
                  
                  <p className="text-gray-600 text-sm mb-4">{template.description}</p>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className="flex items-center space-x-1">
                        <Users className="h-4 w-4 text-gray-400" />
                        <span className="text-sm text-gray-600">{template.uses} uses</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <FileText className="h-4 w-4 text-gray-400" />
                        <span className="text-sm text-gray-600">{template.nodes.length} nodes</span>
                      </div>
                    </div>
                    
                    <button className="flex items-center space-x-2 px-3 py-1 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                      <Download className="h-4 w-4" />
                      <span className="text-sm">Use Template</span>
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {filteredTemplates.length === 0 && (
              <div className="text-center py-12">
                <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500 text-lg">No templates found</p>
                <p className="text-gray-400 text-sm">Try adjusting your search or category filter</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TemplatesModal;